import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useUpdateAllAnnouncementAsReadMutation } from "@/services/api.announcements";
import { CheckCheck } from "lucide-react";
import toast from "react-hot-toast";

interface IProps {
  getAllAnnouncementRefetch: () => void;
  unreadCount: number;
}

export const AnnouncementHeader = ({
  getAllAnnouncementRefetch,
  unreadCount,
}: IProps) => {
  const { t } = useTranslation(["common", "nav"]);
  const [updateMarkAllAsRead, { isLoading }] =
    useUpdateAllAnnouncementAsReadMutation();

  const handleMarkReadAll = async () => {
    const id = toast.loading("Processing...");
    await updateMarkAllAsRead()
      .unwrap()
      .then((res) => {
        getAllAnnouncementRefetch();
        toast.success(
          res.message ?? t("common:inbox.all_announcements_marked_read"),
          { id },
        );
      })
      .catch((e) => {
        toast.error(
          e?.data?.message ??
            e.message ??
            t("common:inbox.failed_to_mark_as_read"),
          { id },
        );
      });
  };

  return (
    <div className="flex items-center justify-between px-4 py-3 border-b bg-background">
      <h3 className="text-[13px] font-semibold tracking-tight">
        {t("nav:modules.announcements")}
      </h3>

      <TooltipProvider delayDuration={300}>
        <div className="flex items-center gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                // Hover text comes from the TooltipContent below; this names the
                // icon-only button for screen readers, which the tooltip cannot.
                aria-label={t("common:a11y.mark_all_as_read")}
                className="h-7 w-7 text-muted-foreground hover:text-primary hover:bg-primary/10"
                disabled={isLoading || unreadCount === 0}
                onClick={handleMarkReadAll}
              >
                <CheckCheck size={14} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              {t("common:inbox.mark_all_as_read")}
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    </div>
  );
};
